import { ApolloQueryResult } from '@apollo/client';
import { message } from 'antd';
import { updateNoteToCard } from 'containers/MainLayout/actions';
import MainActionTypes from 'containers/MainLayout/constants';
import { selectBuyer, selectStore } from 'containers/MainLayout/selectors';
import { call, fork, join, put, select, take, takeLatest } from 'redux-saga/effects';
import translations from 'translations';
import {
  ProductFilter,
  ProductSortField,
  QueryProductSearchArgs,
  ShowcaseItem,
  ShowcaseWidget,
  SortDirection,
  WidgetUnion,
} from 'types/schema';
import {
  addFavorite,
  getBrands,
  getHomeProducts,
  getWidgetConfigV2,
  handlePutItemsToFavorite as handlePutItemsToFavoriteApi,
  removeFavorite,
  setProductNote,
} from 'utils/apollo';
import utilMessages from 'utils/messages';
import { getDateSeed, seededShuffle } from 'utils/seededShuffle';
import { createStoreIdChangeTracker } from 'utils/storeIdChangeTracker';
import { homeFavorite, homeProducts, homeSetProductNote, setWidgets } from './actions';
import ActionTypes from './constants';
import { selectLoading, selectSections, selectWidgets } from './selectors';

const BEST_DEALS_TAG = 'BEST_DEALS';
const homePageStoreTracker = createStoreIdChangeTracker();

function buildBestDealsParams(region: string): QueryProductSearchArgs {
  return {
    sort: [{ field: ProductSortField.CreatedAt, order: SortDirection.Desc }],
    filter: {
      supplierNames: ['KAMEREO'],
      tags: [BEST_DEALS_TAG],
      withInStock: true,
      regionCode: region,
    },
    pagination: { page: 0, size: 100 },
  };
}

// So khớp 2 query bằng cách TRÍCH RIÊNG các field ý nghĩa, miễn nhiễm với:
// - __typename (widget config từ GraphQL đính kèm; checkTypename chỉ strip khi
//   gửi request, tức SAU khi so sánh ở đây)
// - key null/undefined (categoryName:null, limitedTimeOfferPeriod:null...)
// - regionCode (getHomeProducts tự set lại giống nhau cho cả hai)
// - thứ tự phần tử. Khớp -> tái dùng data prefetch; khác -> fetch lại.
function sameQuery(a: QueryProductSearchArgs, b: QueryProductSearchArgs) {
  const key = (p: QueryProductSearchArgs) => {
    const f: any = p.filter || {};
    return JSON.stringify({
      sort: (p.sort || []).map((s: any) => `${s.field}:${s.order}`),
      tags: [...(f.tags || [])].sort(),
      supplierNames: [...(f.supplierNames || [])].sort(),
      categoryIds: [...(f.categoryIds || [])].sort(),
      categoryName: f.categoryName || null,
      withInStock: !!f.withInStock,
      size: p.pagination?.size,
    });
  };
  return key(a) === key(b);
}

function* prefetchBestDeals(region: string) {
  const params = buildBestDealsParams(region);
  const response = yield call(getHomeProducts, params, true);
  return { params: params, response: response };
}

function* getSectionData(section: ShowcaseItem, noBatch = false, prefetchTask: any = null) {
  const { sort, filter } = section;

  const filterConfig: ProductFilter = { ...filter };
  if (filter) {
    if (filterConfig.categoryName && section.id) {
      delete filterConfig.categoryName;
      filterConfig['categoryIds'] = [Number(section.id)];
    }
  }
  const isTagWidget = !!filter?.tags?.length;

  if (isTagWidget) {
    filterConfig.withInStock = true;
  }

  const buyer = yield select(selectBuyer());

  const regionLocal = window.localStorage.getItem('region') || 'HCM';

  filterConfig.regionCode = buyer?.id ? buyer.region : regionLocal;

  const params: QueryProductSearchArgs = {
    sort: sort.length ? sort : [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: filterConfig,
    pagination: { page: 0, size: isTagWidget ? 100 : 10 },
  };

  if (!isTagWidget) {
    params.isBoostKamereoProducts = true;
  }

  yield put(homeProducts.request({ [section.key]: { data: Array.from({ length: 6 }).fill(0), error: false } }));

  // Tái dùng data đã prefetch song song (chỉ với BEST_DEALS) nếu filter khớp;
  // prefetch bắt đầu từ lúc mount nên data thường đã sẵn khi widgets vừa về.
  let response: any;
  if (prefetchTask) {
    const prefetched = yield join(prefetchTask);
    if (prefetched && !prefetched.response?.errors && sameQuery(prefetched.params, params)) {
      response = prefetched.response;
    }
  }
  if (!response) {
    response = yield call(getHomeProducts, params, noBatch);
  }
  if (response.errors) {
    yield put(homeProducts.failure({ [section.key]: { data: [], error: true } }));
  } else {
    const productData = isTagWidget ? seededShuffle(response.data, getDateSeed()).slice(0, 10) : response.data;

    yield put(homeProducts.success({ [section.key]: { data: productData, error: false } }));
  }
}

function* getBrandsData() {
  // FBK first: OZAX, MORI, ACECOOK, VINABEEF, THIENLONG, SG LAGER
  const topBrandsQA = [
    1096, 1093, 1100, 1101, 1102, 447, 52, 51, 50, 49, 48, 47, 46, 45, 44, 43, 42, 41, 40, 39,
  ];
  const topBrandsPROD = [
    1176, 1154, 1242, 1243, 1213, 225, 43, 158, 1, 101, 106, 31, 220, 10, 194, 144, 19, 36, 134, 40, 127, 72,
    246, 3, 45, 4,
  ];

  let brandIds = ['qa', 'development'].includes(process.env.APP_ENV as string) ? topBrandsQA : topBrandsPROD;
  const buyer = yield select(selectBuyer);
  const regionLocal = window.localStorage.getItem('region') || 'HCM';
  if (buyer.region === 'HN' || regionLocal === 'HN') {
    brandIds = brandIds.slice(6);
  }
  const brandResponse = yield call(getBrands, {
    brandIds: brandIds,
    pagination: { page: 0, size: brandIds.length },
  });

  if (brandResponse.errors) {
    yield put(homeProducts.failure({ ['TRUSTED_BRANDS']: { data: [], error: true } }));
  } else {
    const arrangedBrands: any[] = [];
    brandIds.forEach((brandId) => {
      const brand = brandResponse?.data?.find((item) => Number(item.id) === brandId);
      if (brand) {
        arrangedBrands.push(brand);
      }
    });
    yield put(homeProducts.success({ ['TRUSTED_BRANDS']: { data: arrangedBrands, error: false } }));
  }
}

function* loadShowcaseItems(
  currentShowcaseItems: ShowcaseWidget[],
  widgetItems: WidgetUnion[],
  prefetchTask: any = null,
) {
  const loading = yield select(selectLoading());
  const showcaseWidgets = widgetItems.filter((i) => i.type === 'ShowcaseWidget') as unknown as ShowcaseWidget[];
  const newShowcaseConfig = showcaseWidgets.filter(
    (sw) =>
      !currentShowcaseItems.find((csw) => csw.widgetId === sw.widgetId) ||
      !Object.keys(loading).length ||
      loading[sw.widgetId],
  );
  // BEST_DEALS là showcase đầu tiên trong viewport -> fork trước và đi kênh
  // noBatch để không chờ các productSearch section khác trong cùng batch.
  const sectionRank = (w: ShowcaseWidget) =>
    w.item?.filter?.tags?.includes(BEST_DEALS_TAG) ? 0 : w.item?.filter?.tags?.length ? 1 : 2;
  const prioritized = [...newShowcaseConfig].sort((a, b) => sectionRank(a) - sectionRank(b));
  for (const item of prioritized) {
    const section = item.item;
    const isBestDeals = sectionRank(item) === 0;
    // Chỉ section BEST_DEALS mới nhận prefetchTask để tái dùng data bắn song song.
    yield fork(getSectionData, section, isBestDeals, isBestDeals ? prefetchTask : null);
  }
}

export function* getPurchasedItemsMightFavorite(mightFavoriteProductsWidget) {
  const store = yield select(selectStore());
  yield put(
    homeProducts.success({
      [mightFavoriteProductsWidget.widgetId]: {
        data: [],
        error: false,
        loading: true,
        isAdded: false,
      },
    }),
  );
  const params: QueryProductSearchArgs = {
    sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: {
      isMightFavorite: true,
      storeUuid: store.id,
    },
    pagination: { page: 0, size: 100 },
  };
  const response = yield call(getHomeProducts, params);
  if (!response.errors) {
    yield put(
      homeProducts.success({
        [mightFavoriteProductsWidget.widgetId]: {
          data: response.data,
          error: false,
          loading: false,
          isAdded: !response.data.length,
        },
      }),
    );
  } else {
    yield put(
      homeProducts.failure({
        [mightFavoriteProductsWidget.widgetId]: {
          data: [],
          error: true,
          loading: false,
          isAdded: true,
        },
      }),
    );
  }
}

function* fetchWidgets(region: string, prefetchTask: any = null, forceReloadShowcase = false) {
  const response = yield call(getWidgetConfigV2, { regions: [region] });

  if (!(response as unknown as ApolloQueryResult<WidgetUnion[]>).errors) {
    const currentWidgets = yield select(selectWidgets());
    const showcaseWidgets: ShowcaseWidget[] = forceReloadShowcase
      ? []
      : (currentWidgets.filter((i: WidgetUnion) => i?.type === 'ShowcaseWidget') as ShowcaseWidget[]);
    yield fork(loadShowcaseItems, showcaseWidgets, [...response], prefetchTask);
    yield put(setWidgets(response));
  }
}

function* reloadHomePageWidgets(region: string) {
  const bestDealsTask = yield fork(prefetchBestDeals, region);
  yield call(fetchWidgets, region, bestDealsTask, true);
}

function* onRegionChange(actionData: { type: string; payload: string }) {
  const region = actionData.payload || localStorage.getItem('region') || 'HCM';
  yield call(reloadHomePageWidgets, region);
}

function* reloadHomePageOnStoreChange() {
  const store = yield select(selectStore());
  if (!homePageStoreTracker.hasChanged(store?.id)) {
    return;
  }
  const widgets = yield select(selectWidgets());
  if (!widgets.length) {
    return;
  }
  const buyer = yield select(selectBuyer());
  const region = buyer?.region || localStorage.getItem('region') || 'HCM';
  yield call(reloadHomePageWidgets, region);
}

// Nếu buyer về sau với region khác region đã đoán (lần đầu đăng nhập trên máy
// mới) thì fetch lại widgets cho đúng region.
function* refetchWidgetsOnRegionMismatch(usedRegion: string) {
  const loggedIn = !!localStorage.getItem('token');
  const buyerNow = yield select(selectBuyer());
  if (!loggedIn || buyerNow?.id) {
    return;
  }
  yield take(MainActionTypes.FETCH_BUYER_SUCCESS);
  const buyer = yield select(selectBuyer());
  if (buyer?.region && buyer.region !== usedRegion) {
    yield call(fetchWidgets, buyer.region);
  }
}

function* loadWidgets() {
  // KHÔNG chờ FETCH_BUYER_SUCCESS (buyer nằm trong batch nặng, trước đây khiến
  // widgetsRedesign - nguồn data TOP_BANNER/LCP - phải đợi ~10s). Region lấy từ
  // buyer nếu đã có, không thì localStorage (MainLayout saga persist mỗi lần
  // fetch buyer thành công) rồi mới fallback HCM.
  const buyer = yield select(selectBuyer());
  const region = buyer?.region || localStorage.getItem('region') || 'HCM';
  // Bắn BEST_DEALS SONG SONG với widgets ngay từ mount (không đợi widgets về).
  // Khi widgets về, getSectionData sẽ join task này và tái dùng nếu filter khớp.
  // Bỏ qua hẳn prefetch nếu server đã preload BEST_DEALS — getSectionData sẽ render thẳng từ __PRELOADED_STATE__.
  const bestDealsTask = yield fork(prefetchBestDeals, region);
  yield fork(refetchWidgetsOnRegionMismatch, region);
  yield call(fetchWidgets, region, bestDealsTask);
}

function* homeFavoriteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.HOME_FAVORITE);
    yield put(homeFavorite.request());

    const {
      product: { isFavorite, id },
      index,
      section,
    } = payload;

    const response = yield call(isFavorite ? removeFavorite : addFavorite, { id: id });
    if (response.errors) {
      yield put(homeFavorite.failure({ index: index, section: section }));
    } else {
      yield put(homeFavorite.success({ index: index, section: section, isFavorite: !isFavorite }));
    }
  }
}

function* homeSetProductNoteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.HOME_SET_PRODUCT_NOTE);
    yield put(homeSetProductNote.request());

    const { id, note, index, section } = payload;
    const response = yield call(setProductNote, { input: { productId: id, note: note } });
    if (response.errors) {
      yield put(homeSetProductNote.failure(response.errors));
      message.error(translations(utilMessages[payload.note ? 'addItemNoteFailed' : 'deleteItemNoteFailed']));
    } else {
      yield put(homeSetProductNote.success({ note: note, index: index, section: section }));
      yield put(updateNoteToCard(response.product));
      message.success(translations(utilMessages[payload.note ? 'addItemNoteSuccess' : 'deleteItemNoteSuccess']));
    }
  }
}

function* handlePutItemsToFavorite() {
  while (true) {
    const { payload } = yield take(ActionTypes.HANDLE_ADD_ALL_TO_FAVORITE);
    const response = yield call(handlePutItemsToFavoriteApi, { itemUuids: payload });
    if (!response.errors) {
      const sections = yield select(selectSections());
      const isMightFavoriteSection = sections['MIGHT_FAVORITE_PRODUCTS'];
      yield put(
        homeProducts.success({
          ['MIGHT_FAVORITE_PRODUCTS']: {
            ...isMightFavoriteSection,
            isAdded: true,
          },
        }),
      );
    }
  }
}

export default function* saga() {
  // const googleBot = yield call(isGoogleBot);
  // if (!googleBot) {
  //   const homepagePopupTreatment = yield select(selectTreatmentValueByName(TreatmentNames.BUYER_WEB_HOME_PAGE_POPUP));
  //   if (homepagePopupTreatment) {
  //     yield call(loadHomepagePopUp);
  //   }
  // }

  // fork thay vì call: getBrandsData không cần chờ widgets về xong
  const initialStore = yield select(selectStore());
  homePageStoreTracker.sync(initialStore?.id);
  yield fork(loadWidgets);
  yield fork(getBrandsData);
  yield fork(homeFavoriteFlow);
  yield fork(homeSetProductNoteFlow);
  yield fork(handlePutItemsToFavorite);
  yield takeLatest(MainActionTypes.CHANGE_SELECTED_STORE, reloadHomePageOnStoreChange);
  yield takeLatest(MainActionTypes.REGION_CHANGED, onRegionChange);
}
